"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { ValidProduct } from "@/hooks/use-product";

type NutritionalInfo = {
  nutrient: string;
  per100g: string;
  perServing: string;
};

type NutritionalInfoProps = {
  product: ValidProduct;
};

// Animation variants for smooth transitions

const tableContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const tableVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
};

/**
 * Check if a table contains nutritional information by looking for common nutrient keywords
 */
function isNutritionalTable(table: Element): boolean {
  const tableText = table.textContent?.toLowerCase() || "";
  const nutritionalKeywords = [
    "energy",
    "protein",
    "fat",
    "carbohydrate",
    "vitamin",
    "mineral",
    "calcium",
    "iron",
    "sodium",
    "sugar",
    "fiber",
    "calories",
    "kcal",
    "kj",
  ];

  return nutritionalKeywords.some((keyword) => tableText.includes(keyword));
}

/**
 * Parse nutritional information from HTML table in Shopify descriptionHtml
 * Safely handles server-side rendering and validates data existence
 */
function parseNutritionalTable(descriptionHtml: string): NutritionalInfo[] {
  // Early return if no HTML content
  if (!descriptionHtml || typeof descriptionHtml !== "string") {
    return [];
  }

  const nutritionalInfo: NutritionalInfo[] = [];

  // Server-side safe parsing
  if (typeof window !== "undefined") {
    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = descriptionHtml;

      const table = tempDiv.querySelector("table");
      if (!table) return [];

      // Validate that this is actually a nutritional table
      if (!isNutritionalTable(table)) return [];

      const rows = table.querySelectorAll("tr");

      // Skip if table has no data rows (only header)
      if (rows.length <= 1) return [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        const cells = row.querySelectorAll("td");

        if (cells.length >= 2) {
          const nutrientCell = cells[0];
          const valueCell = cells[1];

          if (nutrientCell && valueCell) {
            const nutrient = nutrientCell.textContent?.trim() || "";
            const values = valueCell.textContent?.trim() || "";

            // Validate that we have meaningful data
            if (!(nutrient && values)) continue;

            const valueParts = values.split(" / ");

            // Ensure we have both per100g and perServing values
            if (valueParts.length >= 2 && nutrient) {
              nutritionalInfo.push({
                nutrient: nutrient
                  .replace(/^\*?\s*/, "")
                  .replace(/<[^>]*>/g, ""), // Remove HTML tags
                per100g: valueParts[0]?.trim() || "",
                perServing: valueParts[1]?.trim() || "",
              });
            }
          }
        }
      }
    } catch (error) {
      console.warn("Failed to parse nutritional table:", error);
      return [];
    }
  }

  return nutritionalInfo;
}

/**
 * Extract description text without nutritional table
 * Safely handles server-side rendering and validates content
 */
function extractProductDescription(descriptionHtml: string): string {
  // Early return if no HTML content
  if (!descriptionHtml || typeof descriptionHtml !== "string") {
    return "";
  }

  if (typeof window !== "undefined") {
    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = descriptionHtml;

      // Remove nutritional tables (only remove if they contain nutritional data)
      const tables = tempDiv.querySelectorAll("table");
      tables.forEach((table) => {
        if (isNutritionalTable(table)) {
          table.remove();
        }
      });

      // Remove empty paragraphs and whitespace-only elements
      const emptyParagraphs = tempDiv.querySelectorAll("p");
      emptyParagraphs.forEach((p) => {
        if (!p.textContent?.trim()) {
          p.remove();
        }
      });

      const cleanedHtml = tempDiv.innerHTML.trim();

      // Return empty string if only whitespace or empty tags remain
      const textContent = tempDiv.textContent?.trim() || "";
      return textContent ? cleanedHtml : "";
    } catch (error) {
      console.warn("Failed to extract product description:", error);
      // Fallback to regex approach
    }
  }

  // Server-side fallback using regex
  const cleaned = descriptionHtml
    .replace(/<table[\s\S]*?<\/table>/gi, "")
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/^\s+|\s+$/g, "")
    .trim();

  // Check if there's meaningful content left
  const textOnly = cleaned.replace(/<[^>]*>/g, "").trim();
  return textOnly ? cleaned : "";
}

/**
 * Group nutritional information into categories
 */
function groupNutritionalInfo(nutritionalInfo: NutritionalInfo[]) {
  const macronutrients: NutritionalInfo[] = [];
  const vitamins: NutritionalInfo[] = [];
  const minerals: NutritionalInfo[] = [];
  const other: NutritionalInfo[] = [];

  nutritionalInfo.forEach((info) => {
    const nutrient = info.nutrient.toLowerCase();

    if (
      nutrient.includes("energy") ||
      nutrient.includes("fat") ||
      nutrient.includes("carbohydrate") ||
      nutrient.includes("protein") ||
      nutrient.includes("salt") ||
      nutrient.includes("sugar") ||
      nutrient.includes("saturate")
    ) {
      macronutrients.push(info);
    } else if (
      nutrient.includes("vitamin") ||
      nutrient.includes("thiamin") ||
      nutrient.includes("riboflavin") ||
      nutrient.includes("niacin") ||
      nutrient.includes("folic") ||
      nutrient.includes("biotin") ||
      nutrient.includes("pantothenic")
    ) {
      vitamins.push(info);
    } else if (
      nutrient.includes("calcium") ||
      nutrient.includes("phosphorus") ||
      nutrient.includes("magnesium") ||
      nutrient.includes("iron") ||
      nutrient.includes("zinc") ||
      nutrient.includes("copper") ||
      nutrient.includes("manganese") ||
      nutrient.includes("selenium") ||
      nutrient.includes("chromium") ||
      nutrient.includes("molybdenum") ||
      nutrient.includes("iodine") ||
      nutrient.includes("fluoride")
    ) {
      minerals.push(info);
    } else {
      other.push(info);
    }
  });

  return {
    macronutrients,
    vitamins,
    minerals,
    other,
  };
}

/**
 * Nutritional information table component with animations
 */
function NutritionalTable({
  data,
  title,
}: {
  data: NutritionalInfo[];
  title: string;
}) {
  if (data.length === 0) return null;

  return (
    <motion.div
      animate="visible"
      className="space-y-3"
      initial="hidden"
      layout
      transition={{
        duration: 0.4,
        ease: "easeOut",
      }}
      variants={tableVariants}
    >
      <h4 className="mb-3 font-heading font-semibold text-foreground text-lg lg:text-xl">
        {title}
      </h4>
      <div className="space-y-1">
        {data.map((item) => (
          <div
            className="grid grid-cols-[1fr_120px_120px] gap-2 border-border/30 border-b px-2 py-3 last:border-b-0 hover:bg-muted/20 sm:px-4"
            key={item.nutrient}
          >
            <div className="body-text-base font-medium text-foreground text-optimal">
              {item.nutrient}
            </div>
            <div className="body-text-small text-center font-mono text-muted-foreground">
              {item.per100g}
            </div>
            <div className="body-text-small text-center font-mono text-muted-foreground">
              {item.perServing}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/**
 * Hook to detect user's motion preference for accessibility
 */
function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
}

/**
 * Enhanced nutritional information component with improved design
 * Fetches and validates data from Shopify product fields
 */
export function NutritionalInformation({ product }: NutritionalInfoProps) {
  const prefersReducedMotion = useReducedMotion();

  // Validate product data exists
  if (!(product && product.descriptionHtml)) {
    // Fallback to basic description if available
    if (product?.description) {
      return (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-foreground text-xl">
            About This Product
          </h3>
          <p className="text-muted-foreground text-optimal leading-relaxed">
            {product.description}
          </p>
        </div>
      );
    }
    return null;
  }

  // Parse nutritional data from Shopify descriptionHtml
  const nutritionalData = parseNutritionalTable(product.descriptionHtml);
  const cleanDescription = extractProductDescription(product.descriptionHtml);
  const groupedData = groupNutritionalInfo(nutritionalData);

  const hasNutritionalData = nutritionalData.length > 0;
  const hasCleanDescription = cleanDescription.length > 0;

  // Fallback to basic description if no structured data is available
  if (!(hasNutritionalData || hasCleanDescription)) {
    // Try to use the plain description field as fallback
    if (product.description) {
      return (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-foreground text-xl">
            About This Product
          </h3>
          <p className="text-muted-foreground text-optimal leading-relaxed">
            {product.description}
          </p>
        </div>
      );
    }

    // Last resort: render raw HTML if that's all we have
    return (
      <div className="text-muted-foreground text-optimal leading-relaxed">
        <div
          className="prose prose-sm max-w-none prose-headings:font-heading prose-headings:text-foreground prose-ol:text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:text-muted-foreground"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Shopify HTML content
          dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
        />
      </div>
    );
  }

  // Return separate components for description and nutritional data
  const descriptionComponent = hasCleanDescription ? (
    <div className="space-y-4">
      <h3 className="font-heading font-semibold text-foreground text-xl">
        About This Product
      </h3>
      <div
        className="prose prose-sm max-w-none prose-headings:font-heading prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground text-muted-foreground text-optimal leading-relaxed"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Shopify HTML content
        dangerouslySetInnerHTML={{ __html: cleanDescription }}
      />
    </div>
  ) : null;

  const nutritionalComponent = hasNutritionalData ? (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card className="border-border bg-white shadow-sm">
        <CardHeader className="border-border/50 border-b pb-6">
          <CardTitle className="flex items-center gap-3 font-heading text-foreground text-xl lg:text-2xl">
            Nutritional Information
          </CardTitle>
          {/* Global table headers to reduce redundancy */}
          <div className="grid grid-cols-[1fr_120px_120px] gap-2 px-2 pt-4 font-heading font-medium text-muted-foreground text-sm sm:px-4">
            <div className="text-left">Nutrient</div>
            <div className="text-center">Per 100g</div>
            <div className="text-center">Per Serving</div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8 lg:space-y-10">
          {/* Macronutrients - Always visible */}
          <motion.div
            animate={prefersReducedMotion ? undefined : "visible"}
            initial={prefersReducedMotion ? undefined : "hidden"}
            variants={prefersReducedMotion ? {} : tableContainerVariants}
          >
            <NutritionalTable
              data={groupedData.macronutrients}
              title="Macronutrients"
            />
          </motion.div>

          {/* Accordion section for vitamins and minerals */}
          {(groupedData.vitamins.length > 0 ||
            groupedData.minerals.length > 0 ||
            groupedData.other.length > 0) && (
            <Accordion className="w-full" collapsible type="single">
              <AccordionItem className="border-none" value="vitamins-minerals">
                <AccordionTrigger className="h-auto w-full justify-between p-0 font-heading text-foreground hover:bg-transparent hover:no-underline">
                  <span className="font-semibold text-lg">
                    Vitamins & Minerals
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pt-4">
                  <motion.div
                    animate={prefersReducedMotion ? undefined : "visible"}
                    className="space-y-6"
                    initial={prefersReducedMotion ? undefined : "hidden"}
                    variants={
                      prefersReducedMotion ? {} : tableContainerVariants
                    }
                  >
                    <NutritionalTable
                      data={groupedData.vitamins}
                      title="Vitamins"
                    />
                    <NutritionalTable
                      data={groupedData.minerals}
                      title="Minerals"
                    />
                    {groupedData.other.length > 0 && (
                      <NutritionalTable
                        data={groupedData.other}
                        title="Other Nutrients"
                      />
                    )}
                  </motion.div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>
    </motion.div>
  ) : null;

  return {
    description: descriptionComponent,
    nutritional: nutritionalComponent,
  };
}

/**
 * Product Description Component - extracted from nutritional information
 */
export function ProductDescription({ product }: NutritionalInfoProps) {
  const result = NutritionalInformation({ product });

  // Handle different return types
  if (result && typeof result === "object" && "description" in result) {
    return result.description;
  }

  // If it's a fallback component, return it as is
  return result;
}

/**
 * Nutritional Tables Component - extracted and positioned separately
 */
export function NutritionalTables({ product }: NutritionalInfoProps) {
  const result = NutritionalInformation({ product });

  // Handle different return types
  if (result && typeof result === "object" && "nutritional" in result) {
    return result.nutritional;
  }

  // If no nutritional data, return null
  return null;
}
